import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, fmtDate, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendUp, FilePdf, Printer } from "@phosphor-icons/react";
import { buildIncrementPdf, incrementFileName } from "@/lib/incrementLetter";
import { monthLabel } from "@/lib/payslip";

const nextMonth = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  d.setDate(1); d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const LetterButtons = ({ inc }) => (
  <>
    <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`inc-pdf-${inc.id}`}
      onClick={async () => (await buildIncrementPdf(inc)).save(incrementFileName(inc))}><FilePdf size={13} className="mr-1" />PDF</Button>
    <Button size="sm" variant="ghost" className="h-7 text-[11px]" data-testid={`inc-print-${inc.id}`}
      onClick={async () => { const d = await buildIncrementPdf(inc); d.autoPrint(); window.open(d.output("bloburl"), "_blank", "noopener"); }}><Printer size={13} className="mr-1" />Print</Button>
  </>
);

const IncRow = ({ r, actions }) => (
  <tr data-testid={`inc-row-${r.id}`}>
    <td className="font-mono text-[11.5px]">{r.letter_no}</td>
    <td>{monthLabel(r.effective_month)}</td>
    <td className="num font-mono">{inr(r.old_salary)}</td>
    <td className="num font-mono text-green-700">+{inr(r.increment_amount)} <span className="text-[10.5px]">({r.percent}%)</span></td>
    <td className="num font-mono font-semibold">{inr(r.new_salary)}</td>
    <td className="text-[11.5px]">{r.reason || "—"}<div className="text-muted-foreground">{r.created_by} · {fmtDate(r.created_at)}</div></td>
    <td className="text-right whitespace-nowrap">{actions}</td>
  </tr>
);

const IncTable = ({ rows, renderActions, testid }) => (
  <div className="card-flat overflow-x-auto">
    <table className="data-table" data-testid={testid}>
      <thead><tr><th>Letter No.</th><th>Effective</th><th className="num">Old Salary</th><th className="num">Increment</th><th className="num">New Salary</th><th>Reason</th><th></th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No increments yet.</td></tr>}
        {rows.map((r) => <IncRow key={r.id} r={r} actions={renderActions(r)} />)}
      </tbody>
    </table>
  </div>
);

const GiveIncrement = ({ emp, open, onClose, onDone }) => {
  const [f, setF] = useState({ mode: "amount", value: "", effective_month: nextMonth(), reason: "" });
  const [saving, setSaving] = useState(false);
  const old = Number(emp.monthly_salary) || 0;
  const v = Number(f.value) || 0;
  const inc = f.mode === "amount" ? v : Math.round(old * v) / 100;
  const submit = async () => {
    if (v <= 0) { toast.error("Enter the increment"); return; }
    if (!f.effective_month) { toast.error("Pick the effective month"); return; }
    setSaving(true);
    try { await api.post(`/salary/employees/${emp.id}/increments`, { ...f, value: v }); toast.success("Increment saved"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="inc-dialog">
        <DialogHeader><DialogTitle>Salary Increment · {emp.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="eyebrow">Increment By</Label>
            <Select value={f.mode} onValueChange={(m) => setF({ ...f, mode: m })}>
              <SelectTrigger data-testid="inc-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="amount">Amount (₹)</SelectItem><SelectItem value="percent">Percentage (%)</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1"><Label className="eyebrow">{f.mode === "amount" ? "Amount ₹ *" : "Percent % *"}</Label>
            <Input type="number" min="0" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className="font-mono" data-testid="inc-value" /></div>
          <div className="space-y-1 col-span-2"><Label className="eyebrow">Effective From (month) *</Label>
            <Input type="month" value={f.effective_month} onChange={(e) => setF({ ...f, effective_month: e.target.value })} className="font-mono" data-testid="inc-month" /></div>
          <div className="space-y-1 col-span-2"><Label className="eyebrow">Reason</Label>
            <Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Annual appraisal" data-testid="inc-reason" /></div>
        </div>
        <div className="card-flat p-3 text-[12.5px] grid grid-cols-3 gap-2" data-testid="inc-preview">
          <div><div className="eyebrow">Current</div><b className="font-mono">{inr(old)}</b></div>
          <div><div className="eyebrow">Increment</div><b className="font-mono text-green-700">+{inr(inc)}</b></div>
          <div><div className="eyebrow">New</div><b className="font-mono" data-testid="inc-new-salary">{inr(old + inc)}</b></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="inc-submit">{saving ? "Saving…" : "Save Increment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const IncrementsSection = ({ emp, isAdmin, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try { setRows((await api.get(`/salary/employees/${emp.id}/increments`)).data); } catch (e) { toast.error(formatApiError(e)); }
  }, [emp.id]);
  useEffect(() => { load(); }, [load]);
  const del = async (r) => {
    if (!window.confirm(`Delete increment ${r.letter_no}? Salary goes back to ${inr(r.old_salary)}. If it is the latest letter, the next increment reuses ${r.letter_no}.`)) return;
    try { await api.delete(`/salary/increments/${r.id}`); toast.success("Increment deleted"); load(); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-2" data-testid="inc-section">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setOpen(true)} disabled={!(Number(emp.monthly_salary) > 0)} data-testid="inc-give"><TrendUp size={14} className="mr-1.5" />Give Increment</Button>
      </div>
      <IncTable rows={rows} testid="inc-table" renderActions={(r) => (
        <>
          <LetterButtons inc={r} />
          {isAdmin && r.id === rows[0]?.id && <Button size="sm" variant="ghost" className="h-7 text-[11px] text-destructive" onClick={() => del(r)} data-testid={`inc-delete-${r.id}`}>Delete</Button>}
        </>
      )} />
      {open && <GiveIncrement emp={emp} open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); onChanged(); }} />}
    </div>
  );
};

export const MyIncrements = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { try { setRows((await api.get("/salary/increments/mine")).data); } catch (e) { toast.error(formatApiError(e)); } })(); }, []);
  return <IncTable rows={rows} testid="my-inc-table" renderActions={(r) => <LetterButtons inc={r} />} />;
};
